import React, { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { actions as productTypesActions } from '../../../store/ducks/productType.duck';

import Preloader from '../../../components/other/Preloader/Preloader';

import homeStyles from '../../../constants/homeStyles';
import { IAppState } from '../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { categoriesBreadCrumbs } from './utils/createBreadcrumbs';
import { useFormikCategoryPage } from './hooks/useFormikCategoryPage';
import { CategoryForm } from './components/CategoryForm';

const CategoryPage: React.FC<TPropsFromRedux> = ({
  category,
  clearEdit,
  editLoading,
  editSuccess,
  editError,
  fetch,
  fetchParent,
  fetchCategories,
  loadingById,
  categoryLoading,
  parent,
  categories,
  categoryId,
  me,
  fetchTypes,
  loadingTypes,
}) => {
  const intl = useIntl();
  // const classes = useStyles();
  const homeClasses = homeStyles();
  const navigate = useNavigate();
  const { parentId, id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormikCategoryPage(id || '', parentId || '', categories, category);


  useEffect(() => {
    if (editSuccess || editError) {
      enqueueSnackbar(
        editSuccess
          ? `${intl.formatMessage({ id: 'CATEGORY.SNACKBAR.TITLE' })} ${
              id
                ? `${intl.formatMessage({ id: 'CATEGORY.SNACKBAR.EDIT' })}`
                : `${intl.formatMessage({ id: 'CATEGORY.SNACKBAR.ADD' })}`
            }`
          : `${intl.formatMessage({ id: 'ERROR' })} ${editError}`,
        { variant: editSuccess ? 'success' : 'error' }
      );
    }
    if (editSuccess && categoryId) {
      navigate(-1);
    } else if (editSuccess) {
      navigate(-1);
    }
    return () => {
      clearEdit();
    };
  }, [editSuccess, editError, enqueueSnackbar, clearEdit, id, navigate, intl]);

  useEffect(() => {
    if (id) fetch(+id);
    if (parentId) fetchParent({ parentId: +parentId });
    fetchTypes();
    fetchCategories();
  }, [fetch, id, parentId, fetchCategories, fetchParent]);

  setLayoutSubheader({
    title: `${
      id
        ? `${intl.formatMessage({ id: 'CATEGORY.TITLE.EDIT' })}`
        : `${intl.formatMessage({ id: 'CATEGORY.TITLE.ADD' })}`
    } ${intl.formatMessage({ id: 'CATEGORIES.TITLE' }).toLowerCase()}`,
    breadcrumb: categoriesBreadCrumbs(parent, intl),
  });
  setLayoutFooter({ show: true });

  if (loadingById || categoryLoading || loadingTypes) return <Preloader />;
  return (
    <Row>
      <Col>
        <Card className={homeClasses.classes.container}>
          <CategoryForm
            editLoading={editLoading}
            formik={formik}
            id={id || ''}
            categories={categories}
            me={me}
          />
        </Card>
      </Col>
    </Row>
  );
};

const connector = connect(
  (state: IAppState) => ({
    category: state.categories.category,
    loadingById: state.categories.byIdLoading,
    categoryLoading: state.categories.categoriesLoading,
    categories: state.categories.categories,
    editLoading: state.categories.editLoading,
    editSuccess: state.categories.editSuccess,
    editError: state.categories.editError,
    parent: state.categories.parent,
    categoryId: state.categories.categoryId,
    me: state.profile.me,
    types: state.productTypes.productTypes,
    loadingTypes: state.productTypes.loading,
  }),
  {
    fetch: categoriesActions.fetchByIdRequest,
    fetchParent: categoriesActions.fetchParentRequest,
    clearEdit: categoriesActions.clearEdit,
    fetchCategories: categoriesActions.fetchFullRequest,
    fetchTypes: productTypesActions.fetchRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CategoryPage);
